/**
 * CodePrefixSettings — appended inside EntitySettings.
 *
 * Shows all inventory types from utl_entity_registry with their current
 * effective prefix (entity override or system default). Allows inline edit,
 * per-row revert, and bulk revert-all. Bulk save sends only dirty rows.
 *
 * API: GET/PUT/DELETE /api/v1/org/settings/code-prefixes
 * RBAC: ORG_ENTITY_PROFILE (same gate as parent EntitySettings)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronUp, ChevronsUpDown, RefreshCw, RotateCcw } from 'lucide-react';
import Button from '../common/Button';
import Badge from '../common/Badge';
import FormControl from '../common/FormControl';
import Skeleton from '../common/Skeleton';
import Toast, { ToastMessage } from '../common/Toast';
import { usePermission } from '../../contexts/PermissionContext';
import {
  CodePrefixRow,
  getCodePrefixes,
  saveCodePrefixes,
  revertCodePrefix,
  revertAllCodePrefixes,
} from '../../services/orgService';

const RBAC_ACTIVITY = 'ORG_ENTITY_PROFILE';

type Edits = Record<string, string>;
type SortKey = 'display_name' | 'module_code' | 'effective_prefix' | 'status';
type SortDir = 'asc' | 'desc';

function statusOrder(row: CodePrefixRow, edits: Edits): number {
  const isDirty = (edits[row.type_code] ?? row.effective_prefix) !== row.effective_prefix;
  if (row.is_customised || isDirty) return 1; // Modified
  return 0; // Default
}

const SortTh: React.FC<{
  label: string;
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  className?: string;
}> = ({ label, sortKey, current, dir, onSort, className = '' }) => {
  const active = current === sortKey;
  return (
    <th
      className={`text-left py-2 font-medium cursor-pointer select-none group ${className}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-0.5">
        {label}
        <span className={`transition-opacity ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`}>
          {active
            ? (dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)
            : <ChevronsUpDown size={12} />}
        </span>
      </span>
    </th>
  );
};

const CodePrefixSettings: React.FC = () => {
  const modifyPerm = usePermission(RBAC_ACTIVITY, 'view+modify');
  // canModify is true as long as permissions have finished loading and allow it.
  // Default to true while loading to avoid flash of disabled inputs.
  const canModify = modifyPerm.loading || modifyPerm.allowed;

  const [rows, setRows] = useState<CodePrefixRow[]>([]);
  const [edits, setEdits] = useState<Edits>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [revertingAll, setRevertingAll] = useState(false);
  const [revertingRow, setRevertingRow] = useState<string | null>(null);
  const [moduleFilter, setModuleFilter] = useState('ALL');
  const [sortKey, setSortKey] = useState<SortKey>('display_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCodePrefixes();
      setRows(data);
      const initial: Edits = {};
      data.forEach(r => { initial[r.type_code] = r.effective_prefix; });
      setEdits(initial);
    } catch (err: any) {
      setToast({ type: 'error', message: err?.message ?? 'Failed to load prefix configuration.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirtyRows = useMemo(
    () => rows.filter(r => (edits[r.type_code] ?? r.effective_prefix) !== r.effective_prefix),
    [rows, edits],
  );

  const duplicateErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    const seen: Record<string, string> = {};
    rows.forEach(r => {
      const val = (edits[r.type_code] ?? r.effective_prefix).toUpperCase();
      if (!val) return;
      if (seen[val] && seen[val] !== r.type_code) {
        errors[r.type_code] = `Duplicate of ${seen[val]}`;
        errors[seen[val]] = `Duplicate of ${r.type_code}`;
      } else {
        seen[val] = r.type_code;
      }
    });
    return errors;
  }, [rows, edits]);

  const hasDuplicates = Object.keys(duplicateErrors).length > 0;
  const canSave = dirtyRows.length > 0 && !hasDuplicates && !saving && canModify;

  const handlePrefixChange = (type_code: string, value: string) => {
    setEdits(prev => ({ ...prev, [type_code]: value.toUpperCase() }));
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = dirtyRows.map(r => ({
        type_code: r.type_code,
        prefix: edits[r.type_code],
      }));
      const updated = await saveCodePrefixes(payload);
      setRows(updated);
      const refreshed: Edits = {};
      updated.forEach(r => { refreshed[r.type_code] = r.effective_prefix; });
      setEdits(refreshed);
      setToast({ type: 'success', message: 'Prefix configuration saved.' });
    } catch (err: any) {
      setToast({ type: 'error', message: err?.message ?? 'Failed to save prefix configuration.' });
    } finally {
      setSaving(false);
    }
  };

  const handleRevertRow = async (type_code: string) => {
    setRevertingRow(type_code);
    try {
      const updated = await revertCodePrefix(type_code);
      setRows(prev => prev.map(r => (r.type_code === type_code ? updated : r)));
      setEdits(prev => ({ ...prev, [type_code]: updated.effective_prefix }));
    } catch (err: any) {
      setToast({ type: 'error', message: err?.message ?? 'Failed to revert.' });
    } finally {
      setRevertingRow(null);
    }
  };

  const handleRevertAll = async () => {
    if (!window.confirm('This will reset all custom prefixes to system defaults. Existing codes are not affected. Continue?')) return;
    setRevertingAll(true);
    try {
      const updated = await revertAllCodePrefixes();
      setRows(updated);
      const refreshed: Edits = {};
      updated.forEach(r => { refreshed[r.type_code] = r.effective_prefix; });
      setEdits(refreshed);
      setToast({ type: 'success', message: 'All prefixes reverted to system defaults.' });
    } catch (err: any) {
      setToast({ type: 'error', message: err?.message ?? 'Failed to revert all.' });
    } finally {
      setRevertingAll(false);
    }
  };

  const modules = useMemo(() => {
    const seen = new Set<string>();
    rows.forEach(r => seen.add(r.module_code));
    return ['ALL', ...Array.from(seen).sort()];
  }, [rows]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const visibleRows = useMemo(() => {
    const filtered = moduleFilter === 'ALL' ? rows : rows.filter(r => r.module_code === moduleFilter);
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'display_name') cmp = a.display_name.localeCompare(b.display_name);
      else if (sortKey === 'module_code') cmp = a.module_code.localeCompare(b.module_code);
      else if (sortKey === 'effective_prefix') cmp = a.effective_prefix.localeCompare(b.effective_prefix);
      else if (sortKey === 'status') cmp = statusOrder(a, edits) - statusOrder(b, edits);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, moduleFilter, sortKey, sortDir, edits]);

  const hasCustomised = rows.some(r => r.is_customised);

  return (
    <div className="mt-8 border-t pt-6 p-1">
      <div className="flex items-center justify-between gap-4 mb-3">
        <div>
          <span className="font-semibold text-sm">Coding Prefixes</span>
          <p className="text-xs text-muted mt-0.5">
            Prefixes identify each inventory item (e.g., <code>RSK-1</code>). System defaults are pre-filled. Prefix must be unique across all types within your organisation.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <FormControl.SearchSelect
            value={moduleFilter}
            onChange={v => setModuleFilter(String(v ?? 'ALL'))}
            options={modules.map(m => ({ value: m, label: m === 'ALL' ? 'All modules' : m }))}
            className="w-40"
            noMargin
            data-testid="SEL_prefix_module_filter"
          />
          {canModify && hasCustomised && (
            <Button
              variant="outline"
              icon={RotateCcw}
              onClick={handleRevertAll}
              disabled={revertingAll}
              data-testid="BTN_prefix_revert_all"
            >
              {revertingAll ? 'Reverting…' : 'Revert All'}
            </Button>
          )}
        </div>
      </div>

      <div className="table-list" style={{ height: 420 }}>
        <div className="table-list-scroll">
          <table className="table-list-table" data-testid="TBL_prefix_config">
            <thead>
              <tr>
                <th className="table-list-th table-list-cell--sno">S.No</th>
                <SortTh label="Inventory Type" sortKey="display_name" current={sortKey} dir={sortDir} onSort={handleSort} className="table-list-th" />
                <SortTh label="Module" sortKey="module_code" current={sortKey} dir={sortDir} onSort={handleSort} className="table-list-th" />
                <th className="table-list-th">Prefix</th>
                <th className="table-list-th">Preview</th>
                <SortTh label="Status" sortKey="status" current={sortKey} dir={sortDir} onSort={handleSort} className="table-list-th table-list-cell--status" />
                {canModify && <th className="table-list-th table-list-th--right">Revert</th>}
              </tr>
            </thead>
            <tbody aria-busy={loading} aria-live="polite">
              {loading && (
                <Skeleton.TableRows
                  cols={['sm', 'sm', 'xs', 'md', 'xs', 'pill', ...(canModify ? [{ width: 'icon' as const, align: 'right' as const }] : [])]}
                />
              )}
              {!loading && visibleRows.map((row, idx) => {
                const currentVal = edits[row.type_code] ?? row.effective_prefix;
                const isDirty = currentVal !== row.effective_prefix;
                const isCustomised = row.is_customised || isDirty;
                const dupError = duplicateErrors[row.type_code];
                const isReverting = revertingRow === row.type_code;

                return (
                  <tr
                    key={row.type_code}
                    className={`table-list-row ${dupError ? 'bg-red-50' : ''}`}
                    data-testid={`ROW_prefix_${row.type_code}`}
                  >
                    <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                    <td className="table-list-cell">
                      <span className="table-list-cell-title">{row.display_name}</span>
                    </td>

                    <td className="table-list-cell">
                      <span className="text-xs bg-gray-100 rounded px-1.5 py-0.5">{row.module_code}</span>
                    </td>

                    <td className="table-list-cell">
                      <div className="flex items-center gap-1">
                        <FormControl.Input
                          type="text"
                          maxLength={20}
                          value={currentVal}
                          disabled={!canModify}
                          error={!!dupError}
                          noMargin
                          className="w-32"
                          onChange={e => handlePrefixChange(row.type_code, e.target.value)}
                          data-testid={`INP_prefix_${row.type_code}`}
                        />
                        {row.has_codes_generated && (
                          <span
                            className="text-amber-500 cursor-help"
                            title="Codes already exist for this type. Changing prefix applies to new records only."
                            data-testid={`WARN_prefix_${row.type_code}`}
                          >
                            <AlertTriangle size={14} />
                          </span>
                        )}
                      </div>
                      {dupError && (
                        <p className="text-red-500 text-xs mt-0.5" data-testid={`ERR_prefix_${row.type_code}`}>
                          {dupError}
                        </p>
                      )}
                    </td>

                    <td className="table-list-cell">
                      <span className="text-xs text-muted">
                        {currentVal ? `${currentVal}-1` : '—'}
                      </span>
                    </td>

                    <td className="table-list-cell table-list-cell--status" data-testid={`BADGE_prefix_${row.type_code}`}>
                      <Badge variant={isCustomised ? 'info' : 'neutral'} size="sm">
                        {isCustomised ? 'Modified' : 'Default'}
                      </Badge>
                    </td>

                    {canModify && (
                      <td className="table-list-cell table-list-cell--actions">
                        {row.is_customised && (
                          <button
                            type="button"
                            className="table-list-action table-list-action--edit"
                            onClick={() => handleRevertRow(row.type_code)}
                            disabled={isReverting}
                            title={`Revert to system default (${row.system_default_prefix})`}
                            data-testid={`BTN_revert_${row.type_code}`}
                          >
                            {isReverting
                              ? <RefreshCw size={13} className="animate-spin" />
                              : <RotateCcw size={13} />
                            }
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {canModify && (
        <div className="flex justify-end mt-4">
          <Button
            type="button"
            variant="update"
            icon={Check}
            onClick={handleSave}
            disabled={!canSave}
            data-testid="BTN_prefix_save"
          >
            {saving ? 'Saving…' : dirtyRows.length > 0 ? `Save Changes (${dirtyRows.length})` : 'Save Changes'}
          </Button>
        </div>
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default CodePrefixSettings;
